/**
 * Sanitizes HTML for safe display in comments. Allows only formatting tags.
 */
const ALLOWED_TAGS = ['b', 'i', 'u', 'strong', 'em', 'ol', 'ul', 'li', 'p', 'div', 'br', 'span'];
const ALLOWED_ATTRS: Record<string, string[]> = { span: ['style'], div: ['style'], p: ['style'] };

export function sanitizeHtml(html: string): string {
  if (!html || typeof html !== 'string') return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const walk = (node: Node): Node | null => {
    if (node.nodeType === Node.TEXT_NODE) return node;
    if (node.nodeType !== Node.ELEMENT_NODE) return null;
    const el = node as Element;
    const tag = el.tagName.toLowerCase();
    if (!ALLOWED_TAGS.includes(tag)) {
      return null;
    }
    const allowed = ALLOWED_ATTRS[tag];
    if (allowed) {
      const attrsToRemove: string[] = [];
      for (const a of Array.from(el.attributes)) {
        if (a.name === 'style') {
          const safe = a.value.replace(/[^a-z\-:;\s]/gi, '').replace(/javascript|expression/gi, '');
          el.setAttribute('style', safe);
        } else if (!allowed.includes(a.name)) {
          attrsToRemove.push(a.name);
        }
      }
      attrsToRemove.forEach((a) => el.removeAttribute(a));
    } else {
      for (const a of Array.from(el.attributes)) {
        el.removeAttribute(a.name);
      }
    }
    const children = Array.from(node.childNodes).map(walk).filter(Boolean) as Node[];
    el.replaceChildren(...children);
    return el;
  };
  walk(doc.body);
  return doc.body.innerHTML;
}

/** Returns true if html has no meaningful text content (only tags/whitespace). */
export function isEmptyHtml(html: string): boolean {
  if (!html || typeof html !== 'string') return true;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const text = (doc.body.textContent ?? '').trim();
  return text.length === 0;
}
